import React, { useState, useEffect, useRef } from "react";
import authService from "../../../lib/authService";
import LeftPanel from "./LeftPanel";
import { pushCleverTapEvent } from "../pushCleverTapEvent";

const OTPVerification = ({
  formData,
  requestId,
  onNext,
  onBack,
  onLoginClick,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const inputRefs = useRef([]);
  const [newUser, setNewUser] = useState();

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all fields are filled
    if (newOtp.every((digit) => digit !== "") && newOtp.join("").length === 6) {
      handleVerifyOTP(newOtp.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async (otpValue = otp.join("")) => {
    if (otpValue.length !== 6) {
      setError("Please enter complete OTP");
      return;
    }

    setLoadingVerify(true);
    setError("");

    try {
      const response = await authService.verifyOTP({
        requestId,
        otp: otpValue,
        phoneNumber: parseInt(formData.mobileNumber),
        email: formData.email,
        countryCode: "+91",
        countryName: "India",
      });
      window.gtag("event", "otp_verified");
      pushCleverTapEvent("otp_verified", {
        email: formData.email,
        number: formData.mobileNumber,
        'PageName': 'Sign_up'
      });
      setIsVerified(true);
      const firstTime = response?.result?.firstTimeUser;
      setNewUser(firstTime === false);

      // Get supplier token
      if (firstTime === true) {
        const tokenResponse = await authService.fetchSupplierToken(
          formData.email,
          true
        );

        onNext({
          formData,
          verificationResponse: response,
          token: tokenResponse.token || tokenResponse.data?.token,
        });
      }
    } catch (err) {
      setError(err.message || "Invalid OTP. Please try again.");
      setIsVerified(false);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoadingVerify(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setLoadingResend(true);
    setError("");

    try {
      const response = await authService.sendOTP({
        email: formData.email,
        mobileNumber: parseInt(formData.mobileNumber),
        countryCode: "+91",
        countryName: "India",
      });

      // Check if the API call was successful
      if (response.success && response.result) {
        window.gtag("event", "resend_otp");
        pushCleverTapEvent("resend_otp", {
          email: formData.email,
          number: formData.mobileNumber,
          'PageName': 'Sign_up'
        });
        setResendTimer(30);
        setCanResend(false);
        setIsVerified(false);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        setError("Failed to resend OTP. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Failed to resend OTP. Please try again.");
    } finally {
      setLoadingResend(false);
    }
  };

  const maskedNumber = formData.mobileNumber
    ? `......${formData.mobileNumber.slice(-3)}`
    : "......442";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-[20px] shadow-xl w-full max-w-3xl flex overflow-hidden">
        <LeftPanel />

        {/* Right Panel */}
        <div className="w-[55%] p-8 relative sm:w-full sm:p-3">
          <button
            onClick={() => onLoginClick && onLoginClick()}
            className="absolute top-4 right-4 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {newUser ? (
            <div className="max-w-md mx-auto text-center">
              <h3 className="text-2xl font-bold text-baseBlack mb-6 sm:mb-2 sm:text-lg sm:text-left">
                Account Already Exists
              </h3>
              <p className="text-baseBlack my-8 sm:my-4 text-base2">
                The email or phone number you entered is already associated with
                an existing account. If you have forgotten your login details,
                you can reset your password.{" "}
              </p>
              <button
                onClick={onLoginClick}
                className="w-full bg-brand text-white py-2 sm:py-1 sm:text-sm px-1 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-brand focus:ring-offset-2 mt-4"
              >
                Login Now
              </button>
            </div>
          ) : (
            <div className="max-w-md mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-2 sm:text-lg">
                Enter OTP
              </h3>
              <div className="flex items-center text-gray-600 mb-8">
                <span>OTP sent to register {maskedNumber}</span>
                <button
                  onClick={onBack}
                  className="ml-2 text-brand font-medium hover:underline"
                >
                  Edit
                </button>
              </div>

              <div className="space-y-6 sm:space-y-3">
                <div className="flex justify-center space-x-3 sm:space-x-2">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className={`w-6 h-6 sm:w-8 sm:h-8 text-center text-base sm:text-sm font-semibold px-1 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent outline-none transition-all ${
                        isVerified
                          ? "border-[#0A7205] bg-[#F0FFF0] text-[#000000]"
                          : ""
                      }`}
                      disabled={loadingVerify || isVerified}
                    />
                  ))}
                </div>
                {error && (
                  <div className="text-red-700 rounded-lg my-1 text-sm">
                    {error}
                  </div>
                )}
                {isVerified && (
                  <div className="text-[#0A7205] rounded-lg mb-2 flex items-center justify-end text-sm">
                    <svg
                      className="w-3 h-3 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Number Verified!
                  </div>
                )}
                <div className="text-center">
                  <span className="text-gray-600">Didn't get OTP? </span>
                  <button
                    onClick={handleResendOTP}
                    disabled={!canResend || loadingResend}
                    className={`font-medium ${
                      canResend && !loadingResend
                        ? "text-brand hover:underline"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    {canResend
                      ? loadingResend
                        ? "Sending..."
                        : "Resend OTP"
                      : `Resend in ${resendTimer}s`}
                  </button>
                </div>

                <button
                  onClick={() => handleVerifyOTP()}
                  disabled={loadingVerify || otp.some((digit) => !digit)}
                  className="w-full bg-brand text-white py-2 text-sm rounded-lg font-medium disabled:opacity-50"
                >
                  {loadingVerify ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Verifying...
                    </div>
                  ) : (
                    "Continue"
                  )}
                </button>
              </div>

              <div className="mt-6 text-center">
                <span className="text-[#979797] text-sm">
                  Already have an account?{" "}
                </span>
                <button
                  onClick={onLoginClick}
                  className="text-brand font-medium hover:underline"
                >
                  Login Now
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
